import React, { useState, useEffect } from 'react';
import { Tab } from '@headlessui/react';
import { 
  FiSave, FiShield, FiKey, FiLock, FiMail, FiUsers, 
  FiBook, FiDollarSign, FiStar, FiSettings, FiPlus,
  FiTrash2, FiEdit, FiEye, FiSmartphone, FiUserPlus,
  FiLoader, FiRefreshCw
} from 'react-icons/fi';
import { HiOutlineCheck, HiOutlineArrowSmRight } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { usePermissions } from '../../../../contexts/PermissionsContext';
import { ROLES, getUserPermissions } from '../../../../services/permissionsService';
import permissionsService from '../../../../services/permissionsService';
import permissionsSyncService, { changeUserRole } from '../../../../services/permissionsSyncService';
import { toastService } from '../../../../services/toast';
import DeleteConfirmModal from '../../../common/DeleteConfirmModal';
import { updatePermissions, fetchPermissions, setPermissions } from '../../../../store/slices/permissionsSlice';
import axiosInstance from '../../../../config/axios';

// تعريف كل الصلاحيات المتاحة في النظام
const permissionGroups = {
  courses: {
    title: 'الدورات',
    icon: FiBook,
    permissions: {
      view_courses: 'عرض الدورات',
      create_course: 'إضافة دورة جديدة',
      edit_course: 'تعديل دورة',
      delete_course: 'حذف دورة',
      view_course_content: 'عرض الدورات',
      create_course_content: 'إضافة محتوى للدورة',
      edit_course_content: 'تعديل محتوى الدورة',
      delete_course_content: 'حذف محتوى الدورة',
      view_exams: 'عرض الاختبارات',
      create_exam: 'إضافة اختبار',
      edit_exam: 'تعديل اختبار',
      delete_exam: 'حذف اختبار',
      view_categories: 'عرض التصنيفات',
      create_category: 'إضافة تصنيف',
      edit_category: 'تعديل تصنيف',
      delete_category: 'حذف تصنيف'
    }
  },
  users: {
    title: 'المستخدمين',
    icon: FiUsers,
    permissions: {
      view_admins: 'عرض المشرفين',
      create_admin: 'إضافة مشرف',
      edit_admin: 'تعديل مشرف',
      delete_admin: 'حذف مشرف',
      view_instructors: 'عرض المدربين',
      create_instructor: 'إضافة مدرب',
      edit_instructor: 'تعديل مدرب',
      delete_instructor: 'حذف مدرب',
      view_trainees: 'عرض المتدربين',
      create_trainee: 'إضافة متدرب',
      edit_trainee: 'تعديل متدرب',
      delete_trainee: 'حذف متدرب'
    }
  },
  devices: {
    title: 'الأجهزة',
    icon: FiKey,
    permissions: {
      view_devices: 'عرض الأجهزة',
      manage_devices: 'إدارة الأجهزة',
      delete_devices: 'حذف جهاز'
    }
  },
  payments: {
    title: 'المدفوعات',
    icon: FiDollarSign,
    permissions: {
      view_bank_transfers: 'عرض التحويلات البنكية',
      verify_bank_transfers: 'تأكيد التحويلات البنكية',
      delete_bank_transfers: 'حذف التحويلات البنكية',
      view_electronic_payments: 'عرض المدفوعات الإلكترونية',
      manage_electronic_payments: 'إدارة المدفوعات الإلكترونية',
      delete_electronic_payments: 'حذف المدفوعات الإلكترونية'
    }
  },
  discounts: {
    title: 'الخصومات',
    icon: FiDollarSign,
    permissions: {
      view_coupons: 'عرض كوبونات الخصم',
      create_coupon: 'إنشاء كوبون خصم',
      edit_coupon: 'تعديل كوبون خصم',
      delete_coupon: 'حذف كوبون خصم',
      view_course_discounts: 'عرض خصومات الدورات',
      create_course_discount: 'إنشاء خصم للدورة',
      edit_course_discount: 'تعديل خصم الدورة',
      delete_course_discount: 'حذف خصم الدورة'
    }
  },
  books: {
    title: 'الكتب',
    icon: FiBook,
    permissions: {
      view_books: 'عرض الكتب',
      create_book: 'إضافة كتاب',
      edit_book: 'تعديل كتاب',
      delete_book: 'حذف كتاب'
    }
  },
  instructor_questions: {
    title: 'اسأل مدرب',
    icon: FiUsers,
    permissions: {
      view_instructor_questions: 'عرض أسئلة المدربين',
      answer_instructor_questions: 'الرد على أسئلة المدربين',
      delete_instructor_questions: 'حذف أسئلة المدربين'
    }
  },
  reviews: {
    title: 'التقييمات',
    icon: FiStar,
    permissions: {
      view_reviews: 'عرض التقييمات',
      create_review: 'إضافة تقييم',
      edit_review: 'تعديل تقييم',
      delete_review: 'حذف تقييم'
    }
  },

  settings: {
    title: 'الإعدادات',
    icon: FiSettings,
    permissions: {
      view_settings: 'عرض الإعدادات',
      manage_settings: 'تعديل الإعدادات'
    }
  },
  dashboard_access: {
    title: 'لوحة التحكم',
    icon: FiShield,
    permissions: {
      access_dashboard: 'دخول لوحة التحكم',
      view_financial_statistics: 'مشاهدة الإحصائيات المالية'
    }
  },
  course_access: {
    title: 'مشاهدة الدورات',
    icon: FiEye,
    permissions: {
      view_all_courses: 'مشاهدة جميع الدورات'
    }
  },
  maintenance: {
    title: 'إدارة الصيانة',
    icon: FiRefreshCw,
    permissions: {
      view_site_in_maintenance: 'مشاهدة الموقع أثناء الصيانة'
    }
  }
};

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const SecuritySettings = () => {
  // استخدام Redux
  const dispatch = useDispatch();
  const [selectedTab, setSelectedTab] = useState(0);
  const [settings, setSettings] = useState({
    twoFactorAuth: false,
    emailVerification: true,
    blockIPs: '',
    sslEnabled: true,
    devices: {
      enabled: true,
      maxDevices: 3,
      actionOnExceed: 'block'
    }
  });
  
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [deviceSettingsLoading, setDeviceSettingsLoading] = useState(false);
  const [deviceSettingsSaving, setDeviceSettingsSaving] = useState(false);
  const [isUpdating, setIsUpdating] = useState(null); // لتتبع الصلاحية التي يتم تحديثها حالياً
  const [newGroupName, setNewGroupName] = useState('');
  
  // حفظ واستعادة رقم التبويب النشط من localStorage
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = localStorage.getItem('securitySettingsActiveTab');
    return savedTab ? parseInt(savedTab, 10) : 0;
  });

  // قائمة الأدوار الافتراضية لعرضها أثناء التحميل
  const rolesList = [
    { id: 'admin', name: 'مدير النظام' },
    { id: 'supervisor', name: 'مشرف' },
    { id: 'instructor', name: 'مدرب' },
    { id: 'trainee', name: 'متدرب' },
    { id: 'blocked', name: 'محظور' }
  ];
  
  // تحميل الأدوار والصلاحيات من السيرفر مباشرة
  const loadRolesFromServer = async () => {
    try {
      // بداية بوضع القيم الافتراضية أولاً
      // هذا سيضمن وجود قيم صحيحة حتى قبل الاتصال بالسيرفر
      setGroups(rolesList);
      setSelectedGroup(rolesList[0]); // "مدير النظام" افتراضياً مع كل الصلاحيات
      
      setIsLoading(true);

      
      // نحصل على قائمة الأدوار وصلاحياتها من السيرفر
      const roles = await permissionsSyncService.getRolesWithPermissions();

      if (Array.isArray(roles) && roles.length > 0) {
        // تحويل البيانات إلى التنسيق المطلوب للتطبيق
        const formattedRoles = roles.map(role => {
          // التأكد من أن كل دور يحتوي على الأذونات المناسبة
          let permissions = [];
          
          // إذا كان الدور 'admin' ويجب أن يكون له كل الصلاحيات
          if (role.id === 'admin' || role.name === 'مدير النظام') {
            // تجميع كل الصلاحيات من permissionGroups
            Object.values(permissionGroups).forEach(group => {
              Object.keys(group.permissions).forEach(perm => {
                permissions.push(perm);
              });
            });
          } else if (Array.isArray(role.permissions) && role.permissions.length > 0) {
            // استخدام الأذونات المرسلة من السيرفر
            permissions = role.permissions.map(perm => 
              typeof perm === 'object' && perm.name ? perm.name : perm
            );
          }
          
          return {
            id: role.id,
            name: role.name,
            permissions: permissions
          };
        });

        // تحديث الأدوار فقط إذا كانت تحتوي على بيانات صالحة
        if (formattedRoles.some(role => role.permissions && role.permissions.length > 0)) {
          setGroups(formattedRoles);
          setSelectedGroup(formattedRoles[0]);
        }
      }
    } catch (error) {
      toastService.error('حدث خطأ أثناء تحميل الأدوار والصلاحيات');
    } finally {
      setIsLoading(false);
    }
  };
  
  // استخدام المؤثرات لتحميل البيانات عند تحميل المكون
  useEffect(() => {
    // تحميل قائمة الأدوار من الخادم
    loadRolesFromServer();
    
    // تحميل إعدادات الأجهزة
    loadDeviceSettings();
  }, []);

  const handleAddGroup = async () => {
    if (newGroupName.trim()) {
      setIsLoading(true);
      
      try {
        // إضافة الدور الجديد إلى قاعدة البيانات
        const result = await permissionsSyncService.addNewRole(newGroupName.trim());
        
        if (result.success) {
          // إعادة تحميل الأدوار من السيرفر
          await loadRolesFromServer();
          
          // إعادة تعيين المجموعة المختارة للمجموعة الجديدة
          const newGroup = groups.find(group => group.name === newGroupName.trim());
          if (newGroup) {
            setSelectedGroup(newGroup);
          }
          
          setNewGroupName('');
        }
      } catch (error) {
        // Handle add role error
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleDeleteGroup = async (groupId) => {
    try {
      setIsLoading(true);
      
      // حذف الدور من قاعدة البيانات مباشرة باستخدام الخدمة
      const result = await permissionsSyncService.deleteRole(groupId);
      
      if (result.success) {
        // إذا كانت المجموعة المحذوفة هي المحددة حالياً
        if (selectedGroup?.id === groupId) {
          setSelectedGroup(null);
        }
        
        // إعادة تحميل الأدوار من السيرفر لضمان تحديث القائمة
        await loadRolesFromServer();
        toastService.success('تم حذف الدور بنجاح');
      }
    } catch (error) {
      // Handle delete role error
    } finally {
      setIsLoading(false);
    }
  };

  // دالة للحصول على الاسم العربي للصلاحية
  const getPermissionDisplayName = (permissionName) => {
    // البحث في جميع المجموعات
    for (const groupKey in permissionGroups) {
      const group = permissionGroups[groupKey];
      if (group.permissions && group.permissions[permissionName]) {
        return group.permissions[permissionName];
      }
    }
    // إرجاع الاسم البرمجي إذا لم يتم العثور على الاسم العربي
    return permissionName;
  };
  
  // تم نقل هذا المتغير إلى الأعلى
  
  // تحديث حالة المجموعة وتغيير حالة الصلاحية في الحالة المحلية فقط (بدون حفظ فوري)
  const handleTogglePermission = (groupId, permission) => {
    const currentGroup = groups.find(g => g.id === groupId || String(g.id) === groupId);
    if (!currentGroup) {
      return;
    }
    
    // التأكد من أن صلاحيات المجموعة موجودة (مصفوفة فارغة على الأقل)
    const currentPermissions = Array.isArray(currentGroup.permissions) ? currentGroup.permissions : [];
    
    // التحقق مما إذا كانت الصلاحية موجودة حالياً
    const hasPermission = currentPermissions.includes(permission);
    
    // إنشاء قائمة صلاحيات جديدة بناءً على الحالة الحالية
    let newPermissions = [];
    if (hasPermission) {
      // إذا كانت الصلاحية موجودة، نقوم بإزالتها
      newPermissions = currentPermissions.filter(p => p !== permission);
    } else {
      // إذا لم تكن الصلاحية موجودة، نقوم بإضافتها
      newPermissions = [...currentPermissions, permission];
    }
    
    // تحديث البيانات داخلياً فقط (بدون إرسال إلى السيرفر)
    setGroups(prevGroups => {
      // العثور على المجموعة التي تم تحديثها وتحديث الصلاحيات الخاصة بها
      return prevGroups.map(group => {
        if (group.id === groupId || String(group.id) === groupId) {
          // تحديث المجموعة الحالية
          return { ...group, permissions: newPermissions };
        }
        return group;
      });
    });

    // تحديث المجموعة المحددة أيضاً لتعكس التغييرات فوراً
    setSelectedGroup(prevSelected => {
      if (prevSelected && (prevSelected.id === groupId || String(prevSelected.id) === groupId)) {
        return { ...prevSelected, permissions: newPermissions };
      }
      return prevSelected;
    });
    
    // تعيين حالة الصلاحية التي يتم تحديثها لإظهار مؤشر التحديث
    setIsUpdating(permission);
    setTimeout(() => setIsUpdating(null), 1000); // إزالة المؤشر بعد ثانية واحدة
    
    // تعيين علامة وجود تغييرات غير محفوظة
    setHasUnsavedChanges(true);
  };
  
  // دالة لحفظ جميع التغييرات في صلاحيات الأدوار
  const saveAllChanges = async () => {
    try {
      setIsLoading(true);
      
      if (!selectedGroup) {
        toastService.error('لم يتم تحديد أي دور لحفظ الصلاحيات');
        setIsLoading(false);
        return;
      }
      
      // التأكد من أن الصلاحيات عبارة عن مصفوفة
      const updatedPermissions = Array.isArray(selectedGroup.permissions) ? selectedGroup.permissions : [];
      
      // 1. زر الحفظ يعمل Submit للصلاحيات: تعمل POST/PUT على API
      const response = await permissionsSyncService.saveRolePermissions(selectedGroup.id, updatedPermissions);

      // 2. الـ API يرد بنجاح (status 200)
      if (response.success) {
        // الصلاحيات الجديدة من السيرفر أو المحلية
        const newPermissions = response.data.permissions || updatedPermissions;
        
        // منع تحديث صلاحيات المستخدم الحالي بالخطأ
        // نتحقق من معرف الدور الحالي للمستخدم
        const currentUserRoleId = sessionStorage.getItem('userRoleId');
        
        // تحديث نسخة محلية من الأدوار فقط
        setGroups(prevGroups => {
          return prevGroups.map(group => {
            if (group.id === selectedGroup.id) {
              return { ...group, permissions: newPermissions };
            }
            return group;
          });
        });
        
        // لا نقوم بتحديث Redux أو sessionStorage بصلاحيات دور آخر غير دور المستخدم الحالي
        // نتأكد أن الدور المحدد هو نفس دور المستخدم الحالي قبل تحديث صلاحياته الشخصية
        if (selectedGroup.id === currentUserRoleId) {
          dispatch(setPermissions(newPermissions));
          sessionStorage.setItem('permissions', JSON.stringify(newPermissions));
        }
        
        toastService.success('تم حفظ جميع التغييرات بنجاح');
        setHasUnsavedChanges(false);
      } else {
        toastService.error('فشل حفظ الصلاحيات');
      }
    } catch (error) {
      // Handle save permissions error
      toastService.error('حدث خطأ أثناء حفظ التغييرات');
    } finally {
      setIsLoading(false);
    }
  };

  // تحميل إعدادات الأجهزة من API
  const loadDeviceSettings = async () => {
    try {
      setDeviceSettingsLoading(true);
      const response = await axiosInstance.get('/api/admin/device-settings');
      
      if (response.data && response.data.device_settings) {
        const deviceData = response.data.device_settings;
        
        // تحديث إعدادات الأجهزة في الحالة
        setSettings(prevSettings => ({
          ...prevSettings,
          devices: {
            enabled: deviceData.enabled,
            maxDevices: deviceData.max_devices,
            actionOnExceed: deviceData.action_on_exceed
          }
        }));
      }
    } catch (error) {
      // Handle loading device settings error
      toastService.error('حدث خطأ أثناء تحميل إعدادات الأجهزة');
    } finally {
      setDeviceSettingsLoading(false);
    }
  };

  // حفظ إعدادات الأجهزة إلى API
  const saveDeviceSettings = async () => {
    try {
      setDeviceSettingsSaving(true);
      
      const response = await axiosInstance.post('/api/admin/device-settings', {
        enabled: settings.devices.enabled,
        max_devices: settings.devices.maxDevices,
        action_on_exceed: settings.devices.actionOnExceed
      });
      
      if (response.data && response.data.message) {
        toastService.success(response.data.message);
      }
    } catch (error) {
      // Handle saving device settings error
      toastService.error('حدث خطأ أثناء حفظ إعدادات الأجهزة');
    } finally {
      setDeviceSettingsSaving(false);
    }
  };

  // تم نقل هذه الدالة إلى الأعلى في الملف

  const navigate = useNavigate();

  const tabs = [
    {
      name: 'الأمان',
      icon: FiShield
    },
    {
      name: 'صلاحيات المستخدمين',
      icon: FiUsers
    },
    {
      name: 'الأجهزة',
      icon: FiSmartphone
    }
  ];

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [isLoading, setIsLoading] = useState(false);

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };



  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);

      // حفظ إعدادات الأمان وإعدادات الأجهزة
      // Save settings
      
      // حفظ صلاحيات الأدوار
      // نقوم بتجهيز كائن بصيغة مناسبة لتخزين صلاحيات كل دور
      const rolePermissions = {};
      
      groups.forEach(group => {
        rolePermissions[group.id] = group.permissions;
      });
      
      // تم تصحيح مسارات API في permissionsSyncService.js
      // حفظ صلاحيات كل دور في قاعدة البيانات
      for (const roleId in rolePermissions) {
        await permissionsSyncService.saveRolePermissions(roleId, rolePermissions[roleId]);
      }
      
      // تحديث الصلاحيات في النظام
      window.dispatchEvent(new Event('storage'));
      
      toastService.success('تم حفظ صلاحيات المستخدمين في قاعدة البيانات بنجاح');
    } catch (error) {
      // Handle settings save error
      toastService.error('حدث خطأ أثناء حفظ الإعدادات');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDeviceSettingsChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      devices: {
        ...prev.devices,
        [field]: value
      }
    }));
  };

  return (
    <>
    <div>
      {/* رأس الصفحة */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 flex justify-between items-center">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">إعدادات الأمان</h2>
          <div className="flex items-center gap-1">
            <button
              onClick={() => navigate('/admin/dashboard/settings')}
              className="group relative p-1 text-slate-400 hover:text-slate-600 dark:text-slate-400 dark:hover:text-slate-300"
            >
              <HiOutlineArrowSmRight className="w-5 h-5" />
              <span className="absolute bottom-full mb-2 hidden group-hover:block bg-gray-900 text-white text-xs px-2 py-1 rounded-md whitespace-nowrap right-1/2 transform translate-x-1/2">
                رجوع للإعدادات
              </span>
            </button>
            <button
              onClick={handleSubmit}
              disabled={isLoading}
              className="group relative p-1 text-emerald-600 hover:text-emerald-700 dark:text-emerald-500 dark:hover:text-emerald-400 disabled:opacity-50"
            >
              {isLoading ? (
                <FiLoader className="w-5 h-5 animate-spin" />
              ) : (
                <HiOutlineCheck className="w-5 h-5" />
              )}
              <span className="absolute bottom-full mb-2 hidden group-hover:block bg-gray-900 text-white text-xs px-2 py-1 rounded-md whitespace-nowrap right-1/2 transform translate-x-1/2">
                حفظ التغييرات
              </span>
            </button>
          </div>
        </div>
      </div>

      <div className="p-6">
        <Tab.Group 
          selectedIndex={activeTab} 
          onChange={(index) => {
            setActiveTab(index);
            localStorage.setItem('securitySettingsActiveTab', index.toString());
          }}
        >
          <Tab.List className="flex gap-2 rounded-xl bg-gray-100 dark:bg-gray-700 p-1 mb-6">
            {tabs.map((tab, idx) => (
              <Tab
                key={idx}
                className={({ selected }) =>
                  classNames(
                    'w-full rounded-lg py-3 text-sm font-medium leading-5',
                    'focus:outline-none',
                    'flex items-center justify-center gap-2',
                    selected
                      ? 'bg-white dark:bg-gray-600 text-primary-600 dark:text-primary-400 shadow'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-white/[0.12] dark:hover:bg-gray-500/50'
                  )
                }
              >
                <tab.icon className="w-4 h-4" />
                {tab.name}
              </Tab>
            ))}
          </Tab.List>

          <Tab.Panels>
            {/* تاب الأمان */}
            <Tab.Panel className="focus:outline-none">
              <div className="space-y-6">

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* العمود الأول - إعدادات الأمان الإضافية */}
                  <div className="space-y-6">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                      <div className="flex items-center gap-6 mb-6">
                        <div className="p-3 bg-violet-100 dark:bg-violet-900/30 rounded-lg">
                          <FiShield className="w-6 h-6 text-violet-600 dark:text-violet-400" />
                        </div>
                        <div>
                          <h3 className="text-lg font-medium text-gray-900 dark:text-white">إعدادات الأمان الإضافية</h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400">خيارات متقدمة لتأمين النظام</p>
                        </div>
                      </div>

                      <div className="space-y-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-6">
                            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                              <FiShield className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div>
                              <h4 className="text-base font-medium text-gray-900 dark:text-white">المصادقة الثنائية</h4>
                              <p className="text-sm text-gray-500 dark:text-gray-400">تأمين حسابك باستخدام رمز إضافي</p>
                            </div>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              name="twoFactorAuth"
                              checked={settings.twoFactorAuth}
                              onChange={handleInputChange}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 rounded-full peer dark:bg-gray-700 peer-checked:bg-emerald-600 after:content-[''] after:absolute after:top-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 dose-toggle-fix dose-toggle-clean"></div>
                          </label>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-6">
                            <div className="p-3 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                              <FiMail className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                            </div>
                            <div>
                              <h4 className="text-base font-medium text-gray-900 dark:text-white">تأكيد البريد الإلكتروني</h4>
                              <p className="text-sm text-gray-500 dark:text-gray-400">تفعيل التحقق من البريد الإلكتروني</p>
                            </div>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              name="emailVerification"
                              checked={settings.emailVerification}
                              onChange={handleInputChange}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 rounded-full peer dark:bg-gray-700 peer-checked:bg-emerald-600 after:content-[''] after:absolute after:top-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 dose-toggle-fix dose-toggle-clean"></div>
                          </label>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">عناوين IP المحظورة</label>
                          <textarea
                            name="blockIPs"
                            value={settings.blockIPs}
                            onChange={handleInputChange}
                            placeholder="أدخل عناوين IP المراد حظرها (IP واحد في كل سطر)"
                            rows="4"
                            dir="ltr"
                            className="dose-input w-full resize-none"
                          ></textarea>
                          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            يمكنك إضافة عناوين IP التي تريد حظرها، ضع كل عنوان في سطر منفصل
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* العمود الثاني - إعدادات SSL */}
                  <div className="space-y-6">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-6">
                          <div className="p-3 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                            <FiLock className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                          </div>
                          <div>
                            <h4 className="text-base font-medium text-gray-900 dark:text-white">SSL</h4>
                            <p className="text-sm text-gray-500 dark:text-gray-400">تفعيل شهادة SSL لتأمين الاتصال</p>
                          </div>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            name="sslEnabled"
                            checked={settings.sslEnabled}
                            onChange={handleInputChange}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 rounded-full peer dark:bg-gray-700 peer-checked:bg-emerald-600 after:content-[''] after:absolute after:top-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 dose-toggle-fix dose-toggle-clean"></div>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Tab.Panel>

            {/* تاب صلاحيات المستخدمين */}
            <Tab.Panel className="focus:outline-none">
              <div className="space-y-6">
                {/* بار التنبيه بالتغييرات غير المحفوظة وزر الحفظ */}
                {hasUnsavedChanges && (
                  <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg shadow-sm border border-amber-200 dark:border-amber-800 flex justify-between items-center mb-4">
                    <div className="flex items-center gap-2">
                      <FiRefreshCw className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                      <span className="text-sm font-medium text-amber-700 dark:text-amber-300">يوجد تغييرات غير محفوظة في الصلاحيات</span>
                    </div>
                    <button
                      onClick={saveAllChanges}
                      disabled={isLoading}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium flex items-center gap-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoading ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiSave className="w-4 h-4" />}
                      حفظ جميع التغييرات
                    </button>
                  </div>
                )}
                {/* القسم العلوي - إدارة المجموعات */}
                <div className="grid grid-cols-2 gap-6">
                  {/* اختيار المجموعات */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                    <h3 className="text-lg font-medium mb-4">اختيار المجموعة</h3>
                    <div className="relative">
                      <select
                        value={selectedGroup?.id || ''}
                        onChange={(e) => {
                          const groupId = e.target.value;
                          // تحويل ID إلى نفس نوع البيانات الموجود في المجموعات
                          const group = groups.find(g => String(g.id) === groupId || g.id === parseInt(groupId));
                          setSelectedGroup(group);
                        }}
                        className="mt-1 block w-full h-10 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      >
                        <option value="">اختر مجموعة...</option>
                        {groups.map(group => (
                          <option key={group.id} value={group.id}>
                            {group.name}
                          </option>
                        ))}
                      </select>
                      <div className="absolute left-3 top-1/2 -translate-y-1/2">
                        {selectedGroup && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setRoleToDelete(selectedGroup.id);
                              setDeleteModalOpen(true);
                            }}
                            className="text-red-600 hover:text-red-700 p-1"
                          >
                            <FiTrash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* إضافة مجموعة جديدة */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                    <h3 className="text-lg font-medium mb-4">إضافة مجموعة جديدة</h3>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="اسم المجموعة"
                        value={newGroupName}
                        onChange={(e) => setNewGroupName(e.target.value)}
                        className="dose-input flex-1"
                      />
                      <button
                        onClick={handleAddGroup}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                      >
                        <FiPlus className="w-5 h-5" />
                        <span>إضافة</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* القسم السفلي - عرض الصلاحيات */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-medium">الصلاحيات المتاحة</h3>
                    {selectedGroup && (
                      <div className="text-sm text-gray-500">
                        المجموعة المختارة: <span className="font-medium text-primary-600">{selectedGroup.name}</span>
                      </div>
                    )}
                  </div>

                  {selectedGroup ? (
                    /* عرض الصلاحيات مقسمة حسب المجموعات */
                    <div className="grid grid-cols-2 gap-6">
                      {Object.entries(permissionGroups).map(([key, group]) => {
                        const Icon = group.icon;
                        return (
                          <div key={key} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                            <div className="flex items-center gap-3 mb-4">
                              <div className="p-2 bg-white dark:bg-gray-700 rounded-lg">
                                <Icon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                              </div>
                              <h4 className="text-lg font-medium">{group.title}</h4>
                            </div>
                            <div className="space-y-2">
                              {Object.entries(group.permissions).map(([permKey, label]) => (
                                <label key={permKey} className="flex items-center gap-3 p-2 bg-gray-100 dark:bg-gray-600 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                                  <input
                                    type="checkbox"
                                    checked={selectedGroup?.permissions?.includes(permKey) || false}
                                    onChange={() => selectedGroup && handleTogglePermission(selectedGroup.id, permKey)}
                                    className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                                  />
                                  <span className="text-sm">{label}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <FiUsers className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                      <p className="text-gray-500">اختر مجموعة من القائمة لعرض وتعديل صلاحياتها</p>
                    </div>
                  )}
                </div>
                
                {/* إشعار التغييرات في نهاية الصفحة أيضاً */}
                {hasUnsavedChanges && (
                  <div className="mt-8">
                    <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg shadow-sm border border-amber-200 dark:border-amber-800 flex justify-between items-center mb-4">
                      <div className="flex items-center gap-2">
                        <FiRefreshCw className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                        <span className="text-sm font-medium text-amber-700 dark:text-amber-300">يوجد تغييرات غير محفوظة في الصلاحيات</span>
                      </div>
                      <button
                        onClick={saveAllChanges}
                        disabled={isLoading}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium flex items-center gap-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isLoading ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiSave className="w-4 h-4" />}
                        حفظ جميع التغييرات
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </Tab.Panel>

            {/* تاب الأجهزة */}
            <Tab.Panel className="focus:outline-none">
              <div className="space-y-6">
                {/* تفعيل نظام الأجهزة */}
                <div>
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                      تفعيل نظام الأجهزة
                    </label>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.devices.enabled}
                        onChange={() => handleDeviceSettingsChange('enabled', !settings.devices.enabled)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 rounded-full peer dark:bg-gray-700 peer-checked:bg-emerald-600 after:content-[''] after:absolute after:top-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 dose-toggle-fix dose-toggle-clean"></div>
                    </label>
                  </div>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    تمكين أو تعطيل نظام تتبع الأجهزة للمستخدمين
                  </p>
                </div>

                {/* الحد الأقصى لعدد الأجهزة */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    الحد الأقصى لعدد الأجهزة
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={settings.devices.maxDevices}
                    onChange={(e) => handleDeviceSettingsChange('maxDevices', parseInt(e.target.value))}
                    className="mt-1 input-base"
                    disabled={!settings.devices.enabled}
                  />
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    عدد الأجهزة المسموح للمستخدم بتسجيل الدخول منها
                  </p>
                </div>

                {/* الإجراء عند تجاوز الحد */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    الإجراء عند تجاوز الحد
                  </label>
                  <select
                    value={settings.devices.actionOnExceed}
                    onChange={(e) => handleDeviceSettingsChange('actionOnExceed', e.target.value)}
                    className="mt-1 input-base"
                    disabled={!settings.devices.enabled}
                  >
                    <option value="block">حظر المستخدم تلقائياً</option>
                    <option value="notify">إظهار رسالة تنبيه فقط</option>
                  </select>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    الإجراء الذي سيتم اتخاذه عند محاولة المستخدم تجاوز الحد المسموح به من الأجهزة
                  </p>
                </div>
                
                {/* زر حفظ إعدادات الأجهزة */}
                <div className="mt-6">
                  <button
                    onClick={saveDeviceSettings}
                    disabled={deviceSettingsSaving}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium flex items-center gap-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {deviceSettingsSaving ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiSave className="w-4 h-4" />}
                    حفظ إعدادات الأجهزة
                  </button>
                </div>
              </div>
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
      </div>
    </div>

    <DeleteConfirmModal
      isOpen={deleteModalOpen}
      onClose={() => {
        setDeleteModalOpen(false);
        setRoleToDelete(null);
      }}
      onConfirm={() => {
        handleDeleteGroup(roleToDelete);
        setDeleteModalOpen(false);
        setRoleToDelete(null);
      }}
      itemType="المجموعة"
      itemTitle={groups.find(group => group.id === roleToDelete)?.name || roleToDelete}
    />
    </>
  );
};

export default SecuritySettings;
